// Desk cards above the table: one per prod desk plus the dev desk. Each says what the desk is
// doing, its logins and next run, and (newer API: desks_status) the code it runs and how long
// since its last heartbeat. An office desk (a Mac, not a pod) can't be watched through the
// console, so it says how to watch it instead.

import React from 'react';
import { Box, Button, ButtonBase, Chip, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { OK, WARN, deskLabel, deskNowText, deskPod, fmtClock, heartbeatText, loginDesk, prodName } from './deskModel';
import { DEV_COLOUR, Dot, MONO, Tag, useTones } from './tones';

const sameDay = (iso, nowMs) => new Date(iso).toDateString() === new Date(nowMs).toDateString();
const fmtDayClock = (iso) => {
    try {
        return new Date(iso).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' });
    } catch {
        return iso;
    }
};
const OFFICE_WATCH = 'The office desk is a Mac in the office, so the console cannot relay its screen. '
    + 'Watch it with Screen Sharing over SSH: ssh -L 5900:localhost:5900 office-desk, then open vnc://localhost:5900.';

// What a desk's card says it is doing: idle, or the login its browser has open. A record
// with no heartbeat lately is left over from a restart, so the desk counts as idle.
const deskState = (rec) => (rec && !rec.stale ? `running ${rec.credential}` : 'idle');

export const DeskStatePill = ({ rec }) => {
    const { dark } = useTones();
    const busy = rec && !rec.stale;
    const colours = dark
        ? { bgcolor: busy ? alpha('#5b9cf6', 0.22) : 'action.selected', color: busy ? '#a9c6fa' : 'text.primary' }
        : { bgcolor: busy ? '#e3f2fd' : '#f0f0f0', color: busy ? '#1565c0' : '#555' };
    return (
        <Tooltip title={deskNowText(rec)}>
            <Chip size="small" label={deskState(rec)} sx={{
                fontSize: 12, height: 'auto', minHeight: 22, maxWidth: 240, ...colours,
                '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: '2px' },
            }} />
        </Tooltip>
    );
};

const CARD_SX = {
    display: 'flex', flexDirection: 'column', alignItems: 'stretch', textAlign: 'left',
    borderRadius: 2, border: '1px solid var(--color-border, #e0e0e0)', bgcolor: 'background.paper',
    minHeight: 150, boxSizing: 'border-box', width: '100%', overflow: 'hidden',
};
const CARD_BODY_SX = { display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1.25, p: '14px 16px', textAlign: 'left' };

// Code version and heartbeat age (desks_status); nothing when the API doesn't send one.
const Heartbeat = ({ status, nowMs }) => {
    const t = useTones();
    const hb = heartbeatText(status, nowMs);
    if (!hb) return null;
    return (
        <Box data-heartbeat sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, fontSize: 12, color: t.muted }} className="d-font">
            {hb.version && <Box component="span" sx={{ fontFamily: MONO }}>{hb.version}</Box>}
            {hb.versionNote && <Tag label={hb.versionNote} colour={status.restart_pending ? WARN : OK} outlined />}
            <Box component="span" sx={{ color: hb.stale ? t.bad : t.muted, fontWeight: hb.stale ? 600 : 400 }}>
                · {hb.stale ? `${hb.beat}: stale` : hb.beat}
            </Box>
        </Box>
    );
};

// Watch sits below the card's filter button, not inside it: no nested buttons, and pressing
// it doesn't filter the table. An office desk gets the Screen Sharing hint instead.
const WatchRow = ({ onWatch, office }) => {
    const t = useTones();
    if (office) {
        return (
            <Box sx={{ px: 2, pb: 1.25, mt: 'auto' }}>
                <Tooltip title={OFFICE_WATCH}>
                    <Typography className="d-font" component="span" sx={{ fontSize: 12, color: t.muted }}>
                        Watch: Screen Sharing over SSH
                    </Typography>
                </Tooltip>
            </Box>
        );
    }
    return onWatch ? (
        <Box sx={{ px: 1.25, pb: 1, mt: 'auto' }}>
            <Button size="small" startIcon={<VisibilityIcon />} sx={{ textTransform: 'none' }} onClick={onWatch}>Watch</Button>
        </Box>
    ) : null;
};

// One prod desk: what it is doing, how many logins it owns, and its next run today.
// Pressing it filters the table to the desk's logins; pressing it again shows all desks.
export const DeskCard = ({ desk, rec, count, done, next, selected, onPick, onWatch, nowMs }) => {
    const n = desk.prod_desk;
    const t = useTones();
    const c = t.desk(n);
    return (
        <Box data-desk-card={desk.name} sx={{
            ...CARD_SX,
            borderColor: selected ? c.dot : 'var(--color-border, #e0e0e0)',
            boxShadow: selected ? `inset 0 0 0 1px ${c.dot}` : 'none',
            bgcolor: selected ? alpha(c.dot, 0.12) : 'background.paper',
            '&:hover': { borderColor: c.dot },
        }}>
            {/* No ripple: it lingered as a pale oval on the selected card. The tint and focus ring carry it. */}
            <ButtonBase onClick={onPick} aria-pressed={selected} disableRipple
                sx={{ ...CARD_BODY_SX, '&.Mui-focusVisible': { outline: `2px solid ${c.fg}`, outlineOffset: -2, borderRadius: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography className="d-font" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: 15 }}>
                        <Dot colour={c.dot} />{deskLabel(desk.name)}
                    </Typography>
                    <DeskStatePill rec={rec} />
                </Box>
                <Typography component="span" sx={{ fontFamily: MONO, fontSize: 12, color: t.muted }}>
                    {desk.office ? `office Mac${desk.status?.host && desk.status.host !== 'office' ? ` · ${desk.status.host}` : ''}` : desk.pod || deskPod(desk.name)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2.75 }}>
                    {[[count, 'logins'], [done, 'done today']].map(([v, l]) => (
                        <Box key={l} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography className="d-font" component="span" sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>{v}</Typography>
                            <Typography className="d-font" component="span" sx={{ fontSize: 12, color: t.muted }}>{l}</Typography>
                        </Box>
                    ))}
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography className="d-font" component="span" sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.2, pt: '6px', pb: '4px', overflowWrap: 'anywhere' }}>
                            {next ? `${sameDay(next.at, Date.now()) ? fmtClock(next.at) : fmtDayClock(next.at)} · ${next.id}` : '—'}
                        </Typography>
                        <Typography className="d-font" component="span" sx={{ fontSize: 12, color: t.muted }}>next run</Typography>
                    </Box>
                </Box>
                <Heartbeat status={desk.status} nowMs={nowMs} />
                <Typography className="d-font" component="span" sx={{ fontSize: 12, color: t.soft, lineHeight: 1.45 }}>
                    {!count
                        ? `No logins yet. Assign one with desk: ${n} in Desk/credentials.yaml, ship it, then Enroll now on Prod desk ${n}.`
                        : selected ? 'Showing only its logins. Click again to show all desks.' : 'Click to show only its logins.'}
                </Typography>
            </ButtonBase>
            <WatchRow onWatch={onWatch} office={desk.office} />
        </Box>
    );
};

export const DevCard = ({ desk, rec, onWatch, nowMs }) => {
    const t = useTones();
    return (
        <Box data-desk-card={desk.name} sx={{ ...CARD_SX, bgcolor: 'var(--color-bg-subtle, #fafafa)' }}>
            <Box sx={CARD_BODY_SX}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography className="d-font" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: 15 }}>
                        <Dot colour={DEV_COLOUR.dot} />Dev desk
                    </Typography>
                    <DeskStatePill rec={rec} />
                </Box>
                <Typography component="span" sx={{ fontFamily: MONO, fontSize: 12, color: t.muted }}>{desk.pod || deskPod('dev')}</Typography>
                <Typography className="d-font" component="span" sx={{ fontSize: 13, color: t.soft, lineHeight: 1.45 }}>
                    Recon, try and fix for any login. Never runs a schedule.
                </Typography>
                <Heartbeat status={desk.status} nowMs={nowMs} />
            </Box>
            <WatchRow onWatch={onWatch} />
        </Box>
    );
};

// The Login cell's second line: desk dot, desk, sign-in kind.
export const LoginLine = ({ row, office }) => {
    const t = useTones();
    const n = loginDesk(row);
    return (
        <Typography className="d-font" component="div" sx={{ fontSize: 12, color: t.muted, display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
            {/* the dot stays beside the first line when the text wraps */}
            <Box component="span" sx={{ flex: 'none', alignSelf: 'center' }}><Dot colour={t.desk(n).dot} size={8} /></Box>
            <Box component="span" sx={{ minWidth: 0 }}>Prod desk {n}{office ? ' (office Mac)' : ''} · {row.login === 'sso' ? 'SSO' : 'form login'}</Box>
        </Typography>
    );
};

export const DeskBadge = ({ row, office }) => {
    const n = loginDesk(row);
    const t = useTones();
    const c = t.desk(n);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
            <Box component="span" className="d-font" sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: '2px', borderRadius: 999,
                fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', bgcolor: c.bg, color: c.fg, border: `1px solid ${c.border}`,
            }}>
                <Dot colour={c.dot} />Prod desk {n}
            </Box>
            <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: t.muted }}>
                {office ? 'office Mac' : row.desk_pod || deskPod(prodName(n))}
            </Typography>
        </Box>
    );
};
